import React, { useState, useEffect } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import { getHeaders } from '../../utility/getHeader';
import fetchData from '../../functions/fetchData';
import Layout from '../../layout/Layout';
import Header from '../../components/Header';
import { FaExchangeAlt, FaPlus, FaArrowRight } from 'react-icons/fa';

export default function BankTransfer() {
    const [bankAccounts, setBankAccounts] = useState([]);
    const [transfers, setTransfers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    
    const [formData, setFormData] = useState({
        from_account_id: '',
        to_account_id: '',
        amount: '',
        transfer_date: new Date().toISOString().split('T')[0],
        description: '',
        transfer_charges: 0,
        transfer_type: 'transfer' // 'transfer', 'cash_deposit', 'cash_withdrawal'
    });

    const transferTypes = [
        { value: 'transfer', label: 'Bank to Bank Transfer' },
        { value: 'cash_deposit', label: 'Cash Deposit to Bank' },
        { value: 'cash_withdrawal', label: 'Cash Withdrawal from Bank' }
    ];

    useEffect(() => {
        fetchBankAccounts();
        fetchTransfers();
    }, []);

    const fetchBankAccounts = async () => {
        try {
            await fetchData("bank_accounts", setBankAccounts, "id", {});
        } catch (error) {
            console.error('Error fetching bank accounts:', error);
        }
    };

    const fetchTransfers = async () => {
        setLoading(true);
        try {
            await fetchData("bank_transfers", setTransfers, "id", {});
        } catch (error) {
            console.error('Error fetching transfers:', error);
            toast.error('Failed to fetch bank transfers');
        }
        setLoading(false);
    };

    const resetForm = () => {
        setFormData({
            from_account_id: '',
            to_account_id: '',
            amount: '',
            transfer_date: new Date().toISOString().split('T')[0],
            description: '',
            transfer_charges: 0,
            transfer_type: 'transfer'
        });
    };

    const handleTransferTypeChange = (transferType) => {
        setFormData(prev => ({
            ...prev,
            transfer_type: transferType,
            from_account_id: '',
            to_account_id: '',
            description: transferType === 'cash_deposit' ? 'Cash deposit to bank account' :
                        transferType === 'cash_withdrawal' ? 'Cash withdrawal from bank account' : ''
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validation based on transfer type
        if (formData.transfer_type === 'transfer') {
            if (!formData.from_account_id || !formData.to_account_id) {
                toast.error('Please select both source and destination accounts');
                return;
            }
            
            if (formData.from_account_id === formData.to_account_id) {
                toast.error('Source and destination accounts cannot be the same');
                return;
            }
        } else if (formData.transfer_type === 'cash_deposit') {
            if (!formData.to_account_id) {
                toast.error('Please select bank account for deposit');
                return;
            }
            // Set from_account_id to cash account automatically
            const cashAccount = bankAccounts.find(acc => acc.account_type === 'Cash');
            if (!cashAccount) {
                toast.error('Cash account not found. Please create a cash account first.');
                return;
            }
            formData.from_account_id = cashAccount.id;
        } else if (formData.transfer_type === 'cash_withdrawal') {
            if (!formData.from_account_id) {
                toast.error('Please select bank account for withdrawal');
                return;
            }
            // Set to_account_id to cash account automatically
            const cashAccount = bankAccounts.find(acc => acc.account_type === 'Cash');
            if (!cashAccount) {
                toast.error('Cash account not found. Please create a cash account first.');
                return;
            }
            formData.to_account_id = cashAccount.id;
        }
        
        if (!formData.amount || parseFloat(formData.amount) <= 0) {
            toast.error('Please enter a valid amount');
            return;
        }

        // Check if source account has sufficient balance
        const sourceAccount = bankAccounts.find(acc => acc.id === parseInt(formData.from_account_id));
        if (sourceAccount && sourceAccount.current_balance < parseFloat(formData.amount)) {
            toast.error('Insufficient balance in source account');
            return;
        }

        setLoading(true);
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_BASE_URL}/api/bank-transfers`,
                {
                    ...formData,
                    transfer_type: formData.transfer_type
                },
                getHeaders()
            );

            if (response.data.success) {
                const successMessage = formData.transfer_type === 'cash_deposit' ? 'Cash deposited successfully' :
                                     formData.transfer_type === 'cash_withdrawal' ? 'Cash withdrawn successfully' :
                                     'Bank transfer completed successfully';
                toast.success(successMessage);
                fetchTransfers();
                fetchBankAccounts(); // Refresh balances
                setShowAddModal(false);
                resetForm();
            }
        } catch (error) {
            console.error('Error creating bank transfer:', error);
            toast.error(error.response?.data?.message || 'Failed to complete transfer');
        }
        setLoading(false);
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(amount);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const getAccountInfo = (accountId) => {
        const account = bankAccounts.find(acc => acc.id === accountId);
        return account ? {
            name: account.account_name,
            bank: account.bank_name,
            number: account.account_number,
            balance: account.current_balance
        } : null;
    };

    const AddTransferModal = () => {
        if (!showAddModal) return null;

        const sourceAccount = getAccountInfo(parseInt(formData.from_account_id));
        const destinationAccount = getAccountInfo(parseInt(formData.to_account_id));
        const cashAccount = bankAccounts.find(acc => acc.account_type === 'Cash');

        return (
            <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                <div className="modal-dialog modal-lg">
                    <div className="modal-content">
                        <div className="modal-header bg-info text-white">
                            <h5 className="modal-title">
                                <FaExchangeAlt className="me-2" />
                                {formData.transfer_type === 'cash_deposit' ? 'Cash Deposit' :
                                 formData.transfer_type === 'cash_withdrawal' ? 'Cash Withdrawal' :
                                 'Bank Transfer'}
                            </h5>
                            <button type="button" className="btn-close btn-close-white" onClick={() => setShowAddModal(false)}></button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="modal-body">
                                <div className="row">
                                    {/* Transfer Type Selection */}
                                    <div className="col-12 mb-3">
                                        <label className="form-label">Transaction Type <span className="text-danger">*</span></label>
                                        <div className="row">
                                            {transferTypes.map(type => (
                                                <div key={type.value} className="col-md-4 mb-2">
                                                    <div 
                                                        className={`card h-100 cursor-pointer ${formData.transfer_type === type.value ? 'border-primary bg-light' : 'border-secondary'}`}
                                                        onClick={() => handleTransferTypeChange(type.value)}
                                                        style={{ cursor: 'pointer' }}
                                                    >
                                                        <div className="card-body text-center p-3">
                                                            <input 
                                                                type="radio" 
                                                                name="transfer_type" 
                                                                value={type.value}
                                                                checked={formData.transfer_type === type.value}
                                                                onChange={() => {}}
                                                                className="me-2"
                                                            />
                                                            <div className="fw-semibold">{type.label}</div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* From Account - Only show for normal transfer or cash withdrawal */}
                                    {(formData.transfer_type === 'transfer' || formData.transfer_type === 'cash_withdrawal') && (
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">
                                                {formData.transfer_type === 'cash_withdrawal' ? 'Withdraw From Bank Account' : 'From Account'} 
                                                <span className="text-danger">*</span>
                                            </label>
                                            <select
                                                className="form-control"
                                                value={formData.from_account_id}
                                                onChange={(e) => setFormData({...formData, from_account_id: e.target.value})}
                                                required
                                            >
                                                <option value="">
                                                    {formData.transfer_type === 'cash_withdrawal' ? 'Select Bank Account' : 'Select Source Account'}
                                                </option>
                                                {bankAccounts
                                                    .filter(acc => acc.is_active && (formData.transfer_type === 'cash_withdrawal' ? acc.account_type !== 'Cash' : true))
                                                    .map(account => (
                                                    <option key={account.id} value={account.id}>
                                                        {account.account_name} ({account.bank_name}) - Balance: {formatCurrency(account.current_balance)}
                                                    </option>
                                                ))}
                                            </select>
                                            {sourceAccount && (
                                                <small className="text-muted d-block mt-1">
                                                    Available Balance: <span className="fw-semibold text-success">{formatCurrency(sourceAccount.balance)}</span>
                                                </small>
                                            )}
                                        </div>
                                    )}

                                    {/* Cash Account Info for cash_withdrawal */}
                                    {formData.transfer_type === 'cash_withdrawal' && (
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Withdraw To</label>
                                            <div className="form-control bg-light">
                                                <strong>Cash in Hand</strong>
                                                {cashAccount && (
                                                    <small className="d-block text-muted">
                                                        Current: {formatCurrency(cashAccount.current_balance)}
                                                    </small>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {/* Cash Account Info for cash_deposit */}
                                    {formData.transfer_type === 'cash_deposit' && (
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Deposit From</label>
                                            <div className="form-control bg-light">
                                                <strong>Cash in Hand</strong>
                                                {cashAccount && (
                                                    <small className="d-block text-muted">
                                                        Available: {formatCurrency(cashAccount.current_balance)}
                                                    </small>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {/* To Account - Only show for normal transfer or cash deposit */}
                                    {(formData.transfer_type === 'transfer' || formData.transfer_type === 'cash_deposit') && (
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">
                                                {formData.transfer_type === 'cash_deposit' ? 'Deposit To Bank Account' : 'To Account'} 
                                                <span className="text-danger">*</span>
                                            </label>
                                            <select
                                                className="form-control"
                                                value={formData.to_account_id}
                                                onChange={(e) => setFormData({...formData, to_account_id: e.target.value})}
                                                required
                                            >
                                                <option value="">
                                                    {formData.transfer_type === 'cash_deposit' ? 'Select Bank Account' : 'Select Destination Account'}
                                                </option>
                                                {bankAccounts
                                                    .filter(acc => acc.is_active && 
                                                            acc.id !== parseInt(formData.from_account_id) &&
                                                            (formData.transfer_type === 'cash_deposit' ? acc.account_type !== 'Cash' : true))
                                                    .map(account => (
                                                    <option key={account.id} value={account.id}>
                                                        {account.account_name} ({account.bank_name}) - Balance: {formatCurrency(account.current_balance)}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    )}
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label">Transfer Date <span className="text-danger">*</span></label>
                                        <input
                                            type="date"
                                            className="form-control"
                                            value={formData.transfer_date}
                                            onChange={(e) => setFormData({...formData, transfer_date: e.target.value})}
                                            required
                                        />
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label">Transfer Amount <span className="text-danger">*</span></label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            className="form-control"
                                            value={formData.amount}
                                            onChange={(e) => setFormData({...formData, amount: e.target.value})}
                                            required
                                        />
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label">Transfer Charges</label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            className="form-control"
                                            value={formData.transfer_charges}
                                            onChange={(e) => setFormData({...formData, transfer_charges: parseFloat(e.target.value) || 0})}
                                        />
                                    </div>
                                    <div className="col-12 mb-3">
                                        <label className="form-label">Description</label>
                                        <textarea
                                            className="form-control"
                                            rows="3"
                                            value={formData.description}
                                            onChange={(e) => setFormData({...formData, description: e.target.value})}
                                            placeholder="Transfer description or notes"
                                        ></textarea>
                                    </div>
                                </div>

                                {/* Transfer Summary */}
                                {formData.amount && (
                                    <div className="alert alert-info">
                                        <h6 className="alert-heading">
                                            {formData.transfer_type === 'cash_deposit' ? 'Cash Deposit Summary' :
                                             formData.transfer_type === 'cash_withdrawal' ? 'Cash Withdrawal Summary' :
                                             'Transfer Summary'}
                                        </h6>
                                        
                                        {formData.transfer_type === 'transfer' && formData.from_account_id && formData.to_account_id && (
                                            <div className="row">
                                                <div className="col-md-5">
                                                    <strong>From:</strong><br />
                                                    {sourceAccount?.name}<br />
                                                    <small className="text-muted">{sourceAccount?.bank} - {sourceAccount?.number}</small>
                                                </div>
                                                <div className="col-md-2 text-center">
                                                    <FaArrowRight size={24} className="text-primary mt-2" />
                                                </div>
                                                <div className="col-md-5">
                                                    <strong>To:</strong><br />
                                                    {destinationAccount?.name}<br />
                                                    <small className="text-muted">{destinationAccount?.bank} - {destinationAccount?.number}</small>
                                                </div>
                                            </div>
                                        )}
                                        
                                        {formData.transfer_type === 'cash_deposit' && formData.to_account_id && (
                                            <div className="row">
                                                <div className="col-md-5">
                                                    <strong>From:</strong><br />
                                                    Cash in Hand<br />
                                                    <small className="text-muted">Physical Cash</small>
                                                </div>
                                                <div className="col-md-2 text-center">
                                                    <FaArrowRight size={24} className="text-primary mt-2" />
                                                </div>
                                                <div className="col-md-5">
                                                    <strong>To:</strong><br />
                                                    {destinationAccount?.name}<br />
                                                    <small className="text-muted">{destinationAccount?.bank} - {destinationAccount?.number}</small>
                                                </div>
                                            </div>
                                        )}
                                        
                                        {formData.transfer_type === 'cash_withdrawal' && formData.from_account_id && (
                                            <div className="row">
                                                <div className="col-md-5">
                                                    <strong>From:</strong><br />
                                                    {sourceAccount?.name}<br />
                                                    <small className="text-muted">{sourceAccount?.bank} - {sourceAccount?.number}</small>
                                                </div>
                                                <div className="col-md-2 text-center">
                                                    <FaArrowRight size={24} className="text-primary mt-2" />
                                                </div>
                                                <div className="col-md-5">
                                                    <strong>To:</strong><br />
                                                    Cash in Hand<br />
                                                    <small className="text-muted">Physical Cash</small>
                                                </div>
                                            </div>
                                        )}
                                        
                                        <hr />
                                        <div className="d-flex justify-content-between">
                                            <span>
                                                {formData.transfer_type === 'cash_deposit' ? 'Deposit Amount:' :
                                                 formData.transfer_type === 'cash_withdrawal' ? 'Withdrawal Amount:' :
                                                 'Transfer Amount:'}
                                            </span>
                                            <strong>{formatCurrency(parseFloat(formData.amount) || 0)}</strong>
                                        </div>
                                        {formData.transfer_charges > 0 && (
                                            <div className="d-flex justify-content-between">
                                                <span>Transfer Charges:</span>
                                                <strong>{formatCurrency(formData.transfer_charges)}</strong>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowAddModal(false)}>
                                    Cancel
                                </button>
                                <button type="submit" className="btn btn-info" disabled={loading}>
                                    {loading ? 'Processing...' : 'Complete Transfer'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <Layout>
            <Header title="Bank Transfers" />
            <div className="container-fluid p-4">
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center">
                            <h4 className="mb-0">
                                <FaExchangeAlt className="me-2" />
                                Bank Transfers & Cash Management
                            </h4>
                            <button 
                                className="btn btn-info"
                                onClick={() => {
                                    resetForm();
                                    setShowAddModal(true);
                                }}
                            >
                                <FaPlus className="me-2" />
                                New Transaction
                            </button>
                        </div>
                    </div>
                </div>

                {/* Account Balance Summary */}
                <div className="row mb-4">
                    {bankAccounts.filter(acc => acc.is_active).map(account => (
                        <div key={account.id} className="col-md-3 mb-3">
                            <div className="card">
                                <div className="card-body">
                                    <h6 className="card-title">{account.account_name}</h6>
                                    <p className="card-text">
                                        <small className="text-muted">{account.bank_name}</small><br />
                                        <strong className="text-primary">{formatCurrency(account.current_balance)}</strong>
                                    </p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Transfers Table */}
                <div className="card">
                    <div className="card-header">
                        <h5 className="card-title mb-0">Transfer History</h5>
                    </div>
                    <div className="card-body">
                        {loading ? (
                            <div className="text-center py-4">
                                <div className="spinner-border" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : transfers.length === 0 ? (
                            <div className="text-center py-4">
                                <p className="text-muted mb-0">No bank transfers found. Create your first transfer to get started.</p>
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <table className="table table-striped table-hover">
                                    <thead className="table-dark">
                                        <tr>
                                            <th>Transfer ID</th>
                                            <th>Date</th>
                                            <th>Type</th>
                                            <th>From Account</th>
                                            <th>To Account</th>
                                            <th>Amount</th>
                                            <th>Charges</th>
                                            <th>Description</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {transfers.map((transfer) => {
                                            const fromAccount = getAccountInfo(transfer.from_account_id);
                                            const toAccount = getAccountInfo(transfer.to_account_id);
                                            
                                            return (
                                                <tr key={transfer.id}>
                                                    <td className="fw-semibold">{transfer.transfer_id}</td>
                                                    <td>{formatDate(transfer.transfer_date)}</td>
                                                    <td>
                                                        <span className={`badge ${
                                                            transfer.transfer_type === 'cash_deposit' ? 'bg-success' :
                                                            transfer.transfer_type === 'cash_withdrawal' ? 'bg-warning' :
                                                            'bg-primary'
                                                        }`}>
                                                            {transfer.transfer_type === 'cash_deposit' ? 'Cash Deposit' :
                                                             transfer.transfer_type === 'cash_withdrawal' ? 'Cash Withdrawal' :
                                                             'Bank Transfer'}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        {transfer.transfer_type === 'cash_deposit' ? (
                                                            <div>
                                                                <div className="fw-semibold">Cash</div>
                                                                <small className="text-muted">Physical Cash</small>
                                                            </div>
                                                        ) : fromAccount ? (
                                                            <div>
                                                                <div className="fw-semibold">{fromAccount.name}</div>
                                                                <small className="text-muted">{fromAccount.bank}</small>
                                                            </div>
                                                        ) : '-'}
                                                    </td>
                                                    <td>
                                                        {transfer.transfer_type === 'cash_withdrawal' ? (
                                                            <div>
                                                                <div className="fw-semibold">Cash</div>
                                                                <small className="text-muted">Physical Cash</small>
                                                            </div>
                                                        ) : toAccount ? (
                                                            <div>
                                                                <div className="fw-semibold">{toAccount.name}</div>
                                                                <small className="text-muted">{toAccount.bank}</small>
                                                            </div>
                                                        ) : '-'}
                                                    </td>
                                                    <td className="fw-semibold text-info">{formatCurrency(transfer.amount)}</td>
                                                    <td>{transfer.transfer_charges > 0 ? formatCurrency(transfer.transfer_charges) : '-'}</td>
                                                    <td>{transfer.description || '-'}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <AddTransferModal />
            <ToastContainer />
        </Layout>
    );
}